import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  RefreshCw, 
  Download,
  Loader2,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { localSchedulesService } from '@/services';
import type { ApiLocalSchedule } from '@/types/api';
import { LocalScheduleCard } from './LocalScheduleCard';
import { LocalScheduleFormDialog } from './LocalScheduleFormDialog';
import { ScheduleVerificationSummary } from '@/components/schedules/ScheduleVerificationSummary';
import {
  useScheduleVerification,
  buildVerifyToast,
} from '@/hooks/useScheduleVerification';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { isVerificationTransitional } from '@/components/schedules/VerificationBadge';
import { startVerificationSession } from '@/utils/verificationSession';

interface LocalSchedulesSectionProps {
  controllerId: string;
  controllerName: string;
  localMode?: boolean;
  protocolVersion?: 'v2' | 'v4';
}

export function LocalSchedulesSection({
  controllerId,
  controllerName,
  localMode = false,
  protocolVersion = 'v2',
}: LocalSchedulesSectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ApiLocalSchedule | null>(null);
  const [selectedSlotForCreate, setSelectedSlotForCreate] = useState<number | null>(null);

  const {
    verificationStatus,
    unavailable: verificationUnavailable,
    isLoading: verificationLoading,
    isVerifying,
    awaitingDevice,
    progress: verificationProgress,
    isLora,
    error: verificationError,
    verify,
    refresh: refreshVerification,
  } = useScheduleVerification(controllerId, {
    enabled: !!controllerId && localMode,
    isLora: true,
    controllerName,
  });

  // Query para obtener programaciones locales
  const { 
    data: schedulesData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['local-schedules', controllerId],
    queryFn: async () => {
      const response = await localSchedulesService.getByController(controllerId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar programaciones locales');
      }
      return response.data;
    },
    enabled: !!controllerId && localMode,
    refetchInterval: (query) => {
      const schedules = query.state.data?.schedules ?? [];
      const hasPendingSync = schedules.some(
        (s) => s.syncStatus === 'pending' || s.syncStatus === 'syncing'
      );
      const hasPendingVerification = schedules.some((s) =>
        isVerificationTransitional(s.verificationStatus)
      );
      return hasPendingSync || hasPendingVerification ? 12000 : false;
    },
  });

  const invalidateScheduleQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['local-schedules', controllerId] }),
      queryClient.invalidateQueries({ queryKey: ['schedule-verification', controllerId] }),
    ]);
  };

  // Mutation para sincronizar (plataforma → dispositivo)
  const syncMutation = useMutation({
    mutationFn: async (slots?: number[]) => {
      const response = await localSchedulesService.sync(controllerId, slots ? { slots } : undefined);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al sincronizar');
      }
      return response.data;
    },
    onSuccess: async () => {
      await invalidateScheduleQueries();
      toast({
        title: 'Sincronización iniciada',
        description:
          'Se sincronizan zona horaria, hora del dispositivo y programaciones. En LoRaWAN puede tardar varios minutos.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  /** Enviar una programación concreta al dispositivo */
  const syncByIdMutation = useMutation({
    mutationFn: async (scheduleId: string) => {
      const byId = await localSchedulesService.syncById(scheduleId);
      if (byId.success) return byId.data;
      if (byId.error?.code === 'HTTP_404') {
        const schedule = schedulesData?.schedules?.find((s) => s.id === scheduleId);
        if (schedule) {
          const slotRes = await localSchedulesService.sync(controllerId, {
            slots: [schedule.slotNumber],
          });
          if (!slotRes.success) {
            throw new Error(slotRes.error?.message || 'Error al enviar programación');
          }
          return slotRes.data;
        }
      }
      throw new Error(byId.error?.message || 'Error al enviar programación');
    },
    onSuccess: async () => {
      await invalidateScheduleQueries();
      toast({
        title: 'Programación enviada',
        description:
          'Valores de la plataforma enviados al dispositivo. En LoRaWAN la confirmación puede tardar minutos.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error al enviar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para consultar programaciones del dispositivo (dispositivo → plataforma)
  const fetchMutation = useMutation({
    mutationFn: async () => {
      const response = await localSchedulesService.fetch(controllerId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al consultar programaciones');
      }
      return response.data;
    },
    onSuccess: async () => {
      startVerificationSession({
        controllerId,
        controllerName,
        total: Math.max(1, schedulesData?.schedules?.length ?? 1),
        isLora: true,
      });
      await invalidateScheduleQueries();
      toast({
        title: 'Consulta enviada',
        description:
          'Solicitud de lectura enviada. En LoRa puede tardar varios minutos. Puede cerrar esta ventana; le avisaremos si hay progreso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const schedules = schedulesData?.schedules || [];
  const maxSlots = protocolVersion === 'v4' ? 16 : 10;
  
  // Crear mapa de schedules por slot number para acceso rápido
  const schedulesBySlot = new Map<number, ApiLocalSchedule>();
  schedules.forEach(schedule => {
    schedulesBySlot.set(schedule.slotNumber, schedule);
  });
  
  // Generar array de todos los slots (1-maxSlots) con su estado
  const allSlots = Array.from({ length: maxSlots }, (_, i) => {
    const slotNumber = i + 1;
    const schedule = schedulesBySlot.get(slotNumber);
    return {
      slotNumber,
      schedule: schedule || null,
      isEmpty: !schedule,
    };
  });
  
  // Calcular slots vacíos para el formulario
  const emptySlots = schedulesData?.emptySlots || (() => {
    const usedSlots = new Set(schedules.map(s => s.slotNumber));
    const allSlotsArray = Array.from({ length: maxSlots }, (_, i) => i + 1);
    return allSlotsArray.filter(slot => !usedSlots.has(slot));
  })();

  const handleCreate = () => {
    setSelectedSchedule(null);
    setSelectedSlotForCreate(null);
    setFormDialogOpen(true);
  };

  const handleEdit = (schedule: ApiLocalSchedule) => {
    setSelectedSchedule(schedule);
    setSelectedSlotForCreate(null);
    setFormDialogOpen(true);
  };
  
  const handleSlotClick = (slotNumber: number) => {
    const schedule = schedulesBySlot.get(slotNumber);
    if (schedule) {
      // Slot ocupado: editar
      setSelectedSchedule(schedule);
      setSelectedSlotForCreate(null);
      setFormDialogOpen(true);
    } else {
      // Slot vacío: crear con slot específico
      setSelectedSchedule(null);
      setSelectedSlotForCreate(slotNumber);
      setFormDialogOpen(true);
    }
  };

  const handleSync = () => {
    syncMutation.mutate();
  };

  const handleFetch = () => {
    fetchMutation.mutate();
  };

  if (!localMode) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          El modo local no está activado para esta controladora. Active el modo local para gestionar programaciones locales.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* Header con acciones - Fijo */}
      <div className="flex items-center justify-between flex-shrink-0 pb-4 border-b px-6 pt-4">
        <div>
          <h3 className="text-lg font-display font-semibold">Programaciones Locales</h3>
          <p className="text-sm text-muted-foreground">
            {schedules.length} de {maxSlots} slots utilizados
            {schedules.some((s) => s.syncStatus === 'pending' || s.syncStatus === 'syncing') && (
              <span className="ml-2 text-blue-600">· Sincronizando...</span>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={handleFetch}
            disabled={fetchMutation.isPending}
            title="Importar: dispositivo → plataforma (LoRa asíncrono)"
          >
            <Download className={cn('w-4 h-4 mr-2', fetchMutation.isPending && 'animate-spin')} />
            Importar del dispositivo
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={syncMutation.isPending}
            title="Enviar: plataforma → dispositivo"
          >
            <RefreshCw className={cn('w-4 h-4 mr-2', syncMutation.isPending && 'animate-spin')} />
            Enviar al dispositivo
          </Button>
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={emptySlots.length === 0 && schedules.length >= maxSlots}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva programación
          </Button>
        </div>
      </div>

      {/* Contenido scrolleable */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 max-h-full px-6 pb-4">
        <div className="space-y-3">
        <ScheduleVerificationSummary
          verification={verificationStatus}
          isLoading={verificationLoading}
          isVerifying={isVerifying}
          awaitingDevice={awaitingDevice}
          progress={verificationProgress}
          isLora={isLora}
          error={verificationError}
          unavailable={verificationUnavailable}
          onRefresh={refreshVerification}
          actions={{
            asyncImportHint: true,
            onSyncToDevice: async (scheduleId) => {
              await syncByIdMutation.mutateAsync(scheduleId);
            },
            onImportFromDevice: async () => {
              await fetchMutation.mutateAsync();
            },
          }}
          onVerify={async () => {
            try {
              const data = await verify(true);
              const msg = buildVerifyToast(data, true);
              toast({
                title: msg.title,
                description: msg.description,
                variant: msg.variant,
              });
              await refetch();
            } catch (e) {
              toast({
                title: 'Error al verificar',
                description: e instanceof Error ? e.message : 'Error desconocido',
                variant: 'destructive',
              });
            }
          }}
          className="mt-4"
        />

        {/* Error */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error instanceof Error ? error.message : 'Error al cargar programaciones'}
            </AlertDescription>
          </Alert>
        )}

        {/* Loading */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-5 w-32" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <>
            {/* Vista de todos los slots - Formato de lista más amplio */}
            <div className="pb-2">
              <h4 className="text-sm font-medium text-muted-foreground mb-4 sticky top-0 bg-background z-10 py-2">
                Todos los slots ({schedules.length} ocupados, {emptySlots.length} disponibles)
              </h4>
              <div className="space-y-3">
                {allSlots.map(({ slotNumber, schedule, isEmpty }) => (
                  <div key={slotNumber} className="w-full">
                    {schedule ? (
                      // Slot ocupado: mostrar card clickeable en formato amplio
                      <div 
                        onClick={() => handleSlotClick(slotNumber)}
                        className="cursor-pointer"
                      >
                        <LocalScheduleCard
                          schedule={schedule}
                          onEdit={handleEdit}
                          controllerId={controllerId}
                        />
                      </div>
                    ) : (
                      // Slot vacío: mostrar card vacío clickeable en formato amplio
                      <Card
                        className="p-6 cursor-pointer hover:bg-primary/10 hover:border-primary/50 transition-colors border-2 border-dashed"
                        onClick={() => handleSlotClick(slotNumber)}
                      >
                        <div className="flex items-center justify-center gap-3">
                          <div className="text-center">
                            <div className="text-lg font-semibold text-muted-foreground mb-1">
                              Slot {slotNumber}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Vacío - Click para crear
                            </div>
                          </div>
                          <Plus className="w-6 h-6 text-muted-foreground opacity-50" />
                        </div>
                      </Card>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        </div>
      </div>

      {/* Diálogo de formulario */}
      <LocalScheduleFormDialog
        open={formDialogOpen}
        onOpenChange={(open) => {
          setFormDialogOpen(open);
          if (!open) {
            setSelectedSchedule(null);
            setSelectedSlotForCreate(null);
          }
        }}
        controllerId={controllerId}
        controllerName={controllerName}
        schedule={selectedSchedule}
        availableSlots={emptySlots}
        protocolVersion={protocolVersion}
        defaultSlotNumber={selectedSlotForCreate || undefined}
      />
    </div>
  );
}
